import {
  inject,
  effect,
  EnvironmentInjector,
  runInInjectionContext,
} from '@angular/core';
import { ThemeService, TenantBranding } from '@ams/ui-kit';
import {
  TenantSettingsService,
  BrandingSettings,
} from '../services/tenant-settings.service';

/**
 * Theme initialization provider for the admin portal.
 * Connects TenantSettingsService branding signal to ThemeService.
 *
 * This provider creates an effect that watches the tenant branding signal
 * and applies it to the ThemeService whenever it changes.
 */
export function initializeThemeProvider(injector: EnvironmentInjector) {
  return () => {
    runInInjectionContext(injector, () => {
      const themeService = inject(ThemeService);
      const tenantSettings = inject(TenantSettingsService);

      // Create an effect that watches branding changes and applies them.
      // Pulls the tenant id/name from the same service so we don't clobber
      // what loadPublic() already set with hardcoded placeholders.
      effect(() => {
        const branding = tenantSettings.branding();
        if (branding?.primaryColor) {
          const tenantBranding = mapBrandingToTheme(
            branding,
            tenantSettings.tenant().tenantId,
            tenantSettings.orgName(),
          );
          themeService.setBranding(tenantBranding);
        }
      });
    });
  };
}

/**
 * Maps BrandingSettings from TenantSettingsService to TenantBranding for ThemeService.
 * `tenantId` and `name` come from the tenant signals so this effect doesn't
 * overwrite the values already set by loadPublic().
 */
function mapBrandingToTheme(
  branding: BrandingSettings,
  tenantId: string | undefined,
  name: string | undefined,
): TenantBranding {
  return {
    tenantId: tenantId || 'tenant',
    name: name || 'Membora',
    // Empty strings (not asset paths) when the tenant hasn't uploaded a logo —
    // /assets/logo-{light,dark}.svg don't exist in apps/web, so a fallback path
    // would render as a broken image. The launcher template's @else branch
    // shows a Font Awesome icon when currentLogo() is empty.
    logo: {
      light: branding.logoUrl || '',
      dark: branding.logoUrl || '',
      favicon: branding.faviconUrl,
    },
    theme: {
      primaryColor: branding.primaryColor || '#662D91',
      secondaryColor: branding.secondaryColor,
      accentColor: branding.accentColor,
      headerColor: branding.headerColor,
      sidebarColor: branding.sidebarColor,
      cardColor: branding.cardColor,
      pageHeaderColor: branding.pageHeaderColor,
      pageBackgroundColor: branding.pageBackgroundColor,
      borderRadius: 'rounded',
    },
    customCss: branding.customCss,
  };
}
